import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ENDPOINTS } from "../../lib/config";
import { fetchWithAuth } from "../../lib/authService";

export default function AddEventFormFields({ onSuccess, setAddEvent }) {
  const [venues, setVenues] = useState([]);
  const [venue, setVenue] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState("4");
  const [isLoading, setIsLoading] = useState(true);

  // Set default IST date and time on mount
  useEffect(() => {
    // Get current UTC time and convert to IST (UTC+5:30)
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istTime = new Date(utc + 5.5 * 60 * 60 * 1000);

    const yyyy = istTime.getFullYear();
    const mm = String(istTime.getMonth() + 1).padStart(2, "0");
    const dd = String(istTime.getDate()).padStart(2, "0");
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const timeStr = `${String(istTime.getHours()).padStart(2, "0")}:${String(
      istTime.getMinutes(),
    ).padStart(2, "0")}`;

    setStartDate(dateStr);
    setStartTime(timeStr);
  }, []);

  // Fetch performer's venues
  useEffect(() => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      toast.error("Not authenticated");
      setIsLoading(false);
      return;
    }
    fetchWithAuth(API_ENDPOINTS.VENUE_PERFORMERS)
      .then((r) => r.json())
      .then((data) => {
        if (data.venues && data.venues.length > 0) {
          const allVenues = data.venues.map((v) => v.venue);
          setVenues(allVenues);
          setVenue(allVenues[0]);
        } else {
          toast.error("No venue found for performer");
        }
      })
      .catch(() => toast.error("Error fetching venue"))
      .finally(() => setIsLoading(false));
  }, []);

  const handleVenueChange = (e) => {
    const selectedVenue = venues.find((v) => v.id === e.target.value);
    setVenue(selectedVenue);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!venue) return toast.error("Venue not loaded");
    const dur = parseFloat(duration);
    if (isNaN(dur) || dur < 1 || dur > 24) {
      return toast.error("Duration must be between 1 and 24 hours");
    }
    try {
      // Convert IST input to UTC for API
      const [year, month, day] = startDate.split("-").map(Number);
      const [hours, minutes] = startTime.split(":").map(Number);

      // Create Date object as IST, then convert to UTC by subtracting 5.5 hours
      const istDate = new Date(
        Date.UTC(year, month - 1, day, hours, minutes) - 5.5 * 60 * 60 * 1000,
      );
      const startAt = istDate.toISOString();
      const endAt = new Date(
        istDate.getTime() + dur * 60 * 60 * 1000,
      ).toISOString();

      const payload = {
        name: venue.name,
        venueId: venue.id,
        startAt,
        endAt,
        photoUrl: venue.photoUrl || "",
      };

      const res = await fetchWithAuth(API_ENDPOINTS.EVENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success("Event created successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error("Failed to create event");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setAddEvent(false);
        }
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        className="bg-white backdrop-blur-lg rounded-xl shadow-2xl overflow-hidden w-full max-w-2xl my-2 sm:my-4 min-h-0 max-h-[calc(100vh-16px)] sm:max-h-[calc(100vh-32px)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-2 sm:p-6 bg-white">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl sm:text-3xl text-center w-full font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#651FA8] to-[#8A325D]">
                Create Event
              </h1>
              <button
                onClick={() => setAddEvent(false)}
                className="absolute right-6 top-2 text-2xl text-[#651FA8] hover:text-[#8A325D] mb-2"
              >
                ×
              </button>
            </div>
          </div>
          {/* Scrollable Content */}
          <div className="overflow-y-auto px-4 sm:px-6 flex-1 min-h-0">
            <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-6">
              <div className="w-full flex md:flex-row flex-col items-center justify-center gap-2 sm:gap-4">
                {/* Venue Name Dropdown */}
                <div className="flex-1 w-full">
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Venue Name
                  </label>
                  {venues.length > 1 ? (
                    <select
                      value={venue?.id || ""}
                      onChange={handleVenueChange}
                      className="sm:p-3 p-2 rounded-lg border-2 border-[#651FA8]/30 bg-gray-100 transition-colors max-w-full overflow-hidden w-full"
                      disabled={isLoading}
                    >
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="sm:p-3 p-2 rounded-lg border-2 border-[#651FA8]/30 bg-gray-100 transition-colors max-w-full overflow-hidden">
                      <input
                        value={venue?.name || ""}
                        className="overflow-x-auto w-full whitespace-nowrap bg-gray-100"
                        readOnly
                        disabled
                      />
                    </div>
                  )}
                </div>
                {/* Venue Address */}
                <div className="flex-1 w-full">
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Venue Address
                  </label>
                  <div className="sm:p-3 p-2 rounded-lg border-2 border-[#651FA8]/30 bg-gray-100 transition-colors max-w-full overflow-hidden">
                    <input
                      value={venue?.address?.formatted_address || ""}
                      className="overflow-x-auto w-full whitespace-nowrap bg-gray-100"
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div className="flex gap-2 sm:gap-4">
                <div className="flex-1 w-full">
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Event Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full sm:p-3  p-2 rounded-lg border-2 border-[#651FA8]/30 bg-white focus:ring-2 focus:ring-[#651FA8] outline-none"
                    required
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full sm:p-3  p-2 rounded-lg border-2 border-[#651FA8]/30 bg-white focus:ring-2 focus:ring-[#651FA8] outline-none"
                    required
                  />
                </div>
              </div>
              {/* Duration */}
              <div>
                <label className="block text-sm font-semibold text-[#651FA8] mb-2">
                  Duration (24 hours only)
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={duration}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (isNaN(value)) return setDuration("");
                    if (value >= 1 && value <= 24) {
                      setDuration(value);
                    } else if (value > 24) {
                      toast.warn("Maximum duration allowed is 24 hours");
                      setDuration(24);
                    } else {
                      toast.warn("Minimum duration allowed is 1 hour");
                      setDuration(1);
                    }
                  }}
                  placeholder="4"
                  className="w-full sm:p-3 sm:mb-2 p-2 rounded-lg border-2 border-[#651FA8]/30 focus:ring-2 focus:ring-[#651FA8] outline-none"
                  required
                />
              </div>
            </form>
          </div>
          {/* Fixed Footer with Buttons */}
          <div className="p-3 sm:p-6 border-t border-gray-200 bg-white text-sm flex-shrink-0">
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleSubmit}
                className="w-full py-3 sm:py-4 bg-gradient-to-r from-[#651FA8] to-[#8A325D] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-xl text-base sm:text-lg"
                disabled={isLoading || !venue}
              >
                Create Event →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
